import {
  Handle,
  Position,
  useEdges,
  useNodes,
  useReactFlow,
} from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import {
  Training,
  Theme,
  Evaluation,
  Flag,
  MoreHorizontal,
  Eye,
  Reset,
  Import,
} from '@teo/components/icons';
import { ContentCardLearning, Dropdown, IconButton } from '@teo/components';
import { useEffect, useRef, useState } from 'react';
import { nthLastIndexOf, parseCourseType } from '@/util';
import ThemeNode from './ThemeNode';
import { useGet } from '@/query/fetchHooks';

const CustomNodePath = (node) => {
  const { t } = useTranslation();
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [isHomeConnect, setIsHomeConnect] = useState(true);
  const [themeDisabled, setThemeDisabled] = useState(false);
  const nodes = useNodes();
  const edges = useEdges();

  const newRef = useRef();

  const basePath = window.location.pathname;

  const examplePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 2)
  );

  const [isImportContent, setIsImportContent] = useState(false);
  const [courseImage, setCourseImage] = useState(node?.data?.image);

  useGet(`/coursedetail/${node?.data?.nodeID}`, {
    retry: false,
    enabled: node?.data?.courseType === 'import',
    onSuccess: (result) => {
      setIsImportContent(!!result?.data?.publicationPath);
      setCourseImage(
        result?.data?.image
          ? result?.data?.image
          : result?.data?.courseImage
          ? result?.data?.courseImage
          : node?.data?.image
      );
    },
  });

  const { setNodes, setViewport, viewport } = useReactFlow();
  const [isDraggingNodeDisabled, setIsDraggingNodeDisabled] = useState(false);

  const useOutsideClick = (ref, callback) => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        callback();
      }
    };

    useEffect(() => {
      document.addEventListener('click', handleClick);

      return () => {
        document.removeEventListener('click', handleClick);
      };
    });
  };

  useOutsideClick(newRef, () => {
    setIsOpenDropdown(false);
  });

  useEffect(() => {
    setNodes((nodes) =>
      nodes.map((node) =>
        node.id === node.id
          ? { ...node, draggable: !isDraggingNodeDisabled }
          : node
      )
    );

    setViewport({ ...viewport, panOnDrag: !isDraggingNodeDisabled });
  }, [isDraggingNodeDisabled, setNodes, node.id, setViewport, viewport]);
  useEffect(() => {
    if (node?.data.type == 'homepage') {
      const homeConnect = edges?.find((edg) => edg?.source == node?.id);
      setIsHomeConnect(!!homeConnect);
    }
  }, [edges]);

  useEffect(() => {
    const rawSelected = nodes.filter((n) => n.selected);
    const isDisable = rawSelected?.find(
      (nds) =>
        nds?.data?.courseType == 'milestone' ||
        nds?.data?.courseType == 'learningpath'
    );
    if (isDisable) {
      setThemeDisabled(true);
    } else {
      setThemeDisabled(false);
    }
  }, [nodes]);

  return (
    <div className={`w-full rounded-lg `}>
      {node?.data.type != 'homepage' && (
        <>
          <Handle
            type="target"
            position={Position.Top}
            id="b"
            className="!pointer-events-none !opacity-0"
          />
          <Handle type="target" position={Position.Left} id="a-1" />
          <Handle type="source" position={Position.Left} id="a" />
        </>
      )}
      <div className="relative flex h-full min-h-24 w-full">
        {node?.data?.notConnect && (
          <div className="absolute -top-[1px] -left-[1px] h-[calc(100%_+_2px)] w-[calc(100%_+_2px)] rounded-lg border border-error-04"></div>
        )}
        {node?.data?.type !== 'homepage' && (
          <div className="absolute right-2 top-2 z-[11]" ref={newRef}>
            <Dropdown>
              <Dropdown.Trigger>
                <IconButton
                  Icon={MoreHorizontal}
                  variant="secondary"
                  className={`settings_btn w-5 min-w-5 text-[#7d7d81] ${
                    isOpenDropdown ? 'block' : 'hidden'
                  }`}
                  onClick={() => setIsOpenDropdown(!isOpenDropdown)}
                />
              </Dropdown.Trigger>
              <Dropdown.Content
                style={{
                  visibility: 'visible',
                  right: '0',
                  top: '25px',
                  position: 'absolute',
                }}
              >
                <Dropdown.Actions>
                  <Dropdown.Button
                    onClick={() => {
                      if (node?.data?.type === 'training') {
                        window.open(
                          `${basePath}/${node?.data?.courseType}/${node?.data?.trainingId}/preview`,
                          '_blank'
                        );
                      } else if (node?.data?.type === 'learningpath') {
                        window.open(
                          `${basePath}/theme/${node?.data?.nodeID}/preview`,
                          '_blank'
                        );
                      } else {
                        window.open(
                          `${examplePath}/${parseCourseType(
                            node?.data?.publicationCourseType
                          )}/${
                            node?.data?.coursecontentId
                          }/preview?les=0&new_tab=true`,
                          '_blank'
                        );
                      }
                    }}
                  >
                    <Eye className="mr-2 h-6 w-6" />
                    <p className="text-sm text-[#231F20]">Preview</p>
                  </Dropdown.Button>
                  {node?.data?.courseType !== 'learningpath' &&
                    node?.data?.courseType !== 'milestone' &&
                    !themeDisabled && (
                      <Dropdown.Button
                        onClick={() =>
                          node?.data?.handleMakeTheme(node, nodes, edges)
                        }
                      >
                        <Theme className="mr-2 h-6 w-6" />
                        <p className="text-sm text-[#231F20]">Make theme</p>
                      </Dropdown.Button>
                    )}
                  <Dropdown.Button
                    onClick={() =>
                      node?.data?.handleReplaceNode(node.id, nodes)
                    }
                  >
                    <Reset className="mr-2 h-6 w-6" />
                    <p className="text-sm text-[#231F20]">Replace</p>
                  </Dropdown.Button>
                  <Dropdown.Button
                    onClick={() =>
                      node?.data?.handleRemoveNode(
                        {
                          id: node.id,
                          type: node.type,
                          data: {
                            nodeID: node?.data?.nodeID,
                            type: node?.data?.type,
                          },
                        },
                        nodes,
                        edges
                      )
                    }
                  >
                    <img
                      src="/images/icons/trash_icon_red.svg"
                      alt="delete button"
                      className="mr-2 w-5"
                    />
                    <p className="text-sm text-[#231F20]">Delete</p>
                  </Dropdown.Button>
                </Dropdown.Actions>
              </Dropdown.Content>
            </Dropdown>
          </div>
        )}

        {node?.data.type === 'homepage' && (
          <ContentCardLearning
            imageUrl={node?.data?.image}
            text={node?.data.title}
          />
        )}

        {node?.data.courseType === 'course' && (
          <ContentCardLearning
            imageUrl={node?.data?.image}
            text={node?.data?.title}
            tagTitle={t('links.course')}
            tagVariant="type"
          />
        )}

        {node?.data.courseType === 'assessment' && (
          <ContentCardLearning
            imageUrl={node?.data?.image}
            text={node?.data?.title}
            tagTitle={t('links.assessment')}
            tagVariant="type"
            tagType="red"
          />
        )}

        {node?.data.courseType === 'leervacature' && (
          <ContentCardLearning
            imageUrl={node?.data?.image}
            text={node?.data?.title}
            tagTitle={t('links.vacancy')}
            tagVariant="error"
            tagType="red"
          />
        )}

        {node?.data?.courseType === 'import' && (
          <ContentCardLearning
            imageUrl={isImportContent && courseImage}
            icon={Import}
            text={node?.data?.title}
            tagTitle={t('pages.learning_path.imported')}
            tagVariant="type"
            color="light_blue"
            tagType="light_blue"
          />
        )}

        {node?.data?.courseType === 'training' && (
          <ContentCardLearning
            icon={Training}
            text={node?.data?.title}
            tagTitle={t('pages.learning_path.training')}
            tagVariant="type"
            color="green"
            tagType="green"
          />
        )}

        {node?.data?.courseType === 'theme' && (
          <ContentCardLearning
            icon={Theme}
            text={node?.data?.title}
            tagTitle={t('pages.learning_path.theme')}
            tagVariant="type"
            color="dark_blue"
            tagType="dark_blue"
          />
        )}

        {node?.data?.courseType === 'milestone' && (
          <ContentCardLearning
            icon={Flag}
            text={node?.data?.title}
            tagTitle={t('pages.learning_path.milestone')}
            tagVariant="type"
            color="blue"
            tagType="blue"
          />
        )}

        {node?.data?.courseType === 'evaluation' && (
          <ContentCardLearning
            icon={Evaluation}
            text={node?.data?.title}
            tagTitle={t('pages.learning_path.evaluation')}
            tagVariant="type"
            color="purple"
            tagType="purple"
          />
        )}
        {node?.data?.courseType === 'learningpath' && (
          <ThemeNode
            node={node}
            setNodes={setNodes}
            setIsDraggingNodeDisabled={setIsDraggingNodeDisabled}
          />
        )}
      </div>

      {node?.data.type != 'homepage' && (
        <>
          <Handle type="target" position={Position.Right} id="c-1" />
          <Handle type="source" position={Position.Right} id="c" />
        </>
      )}
      {node?.data.type != 'homepage' && (
        <Handle type="source" position={Position.Bottom} id="d" />
      )}

      {node?.data.type == 'homepage' && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="d"
          className={`${
            isHomeConnect &&
            '!max-h-[0px] !min-h-[0px] !min-w-[0px] !max-w-[0px] overflow-hidden opacity-[0]'
          }`}
        />
      )}
    </div>
  );
};

export default CustomNodePath;
